import get from 'lodash.get';
import { TRADE, CONSUMER } from 'dibs-buyer-layout/exports/userTypeEnums';
import { perfFlags, getPerfQp } from 'dibs-buyer-layout/exports/perfFlags';
import { PAGE_SIZE_OPTIONS } from '../constants/sbConstants';
import { sbAdMap, sbAdExcludeMap, TILE_AD_KEY } from '../constants/sbAdMap';
import { sbTradeAdMap } from '../constants/sbTradeAdMap';

/* Constants */
import {
    QP_PAGE_SIZE_VALUE_DEFAULT,
    QP_PAGE_SIZE_VALUE_MAX,
} from '../constants/queryParamConstants';

export const adLimitMap = PAGE_SIZE_OPTIONS.reduce((map, option) => {
    map[option.size] = option.adLimit;
    return map;
}, {});

const userTypeAdMap = {
    [CONSUMER]: sbAdMap,
    [TRADE]: sbTradeAdMap,
};

const HOLIDAY_COLLECTION_SEGMENT = 'holiday-gift-guide';

const getUriRefSegments = uriRef =>
    uriRef
        .split('?')[0]
        .split('/')
        .filter(segment => segment !== '');

const findAdKeyByUriRef = ({ uriRef, adMap, adKey }) => {
    let segments = getUriRefSegments(uriRef);
    const lastSegment = segments[segments.length - 1];
    const adConfigKeyList = Object.keys(adMap);

    // holiday collection tile ads should show only when no filters applied
    if (
        adKey === TILE_AD_KEY &&
        segments.includes(HOLIDAY_COLLECTION_SEGMENT) &&
        !lastSegment.includes(HOLIDAY_COLLECTION_SEGMENT)
    ) {
        segments = segments.filter(segment => segment !== HOLIDAY_COLLECTION_SEGMENT);
    }

    return segments.find(segment => adConfigKeyList.includes(segment)) || 'default';
};

export const getAdData = ({ uriRef, adKey, userType = CONSUMER }) => {
    // 1. get ad config for user type or collection
    const adMap = userTypeAdMap[userType];
    // 2. find ad key that matches uri segment e.g. /fashion/ and is included in ad config
    const key = findAdKeyByUriRef({ uriRef, adMap, adKey });
    // 3. get ad data by ad key (placement ad type e.g. `productTile`) unless it is excluded for that page
    const excludeKey = findAdKeyByUriRef({ uriRef, adMap: sbAdExcludeMap });
    const excludeList = sbAdExcludeMap[excludeKey];
    if (excludeList && excludeList.includes(adKey)) {
        return null;
    }
    return get(adMap[key], adKey);
};

function getAdCountForPage({ pageSize = QP_PAGE_SIZE_VALUE_DEFAULT, uriRef, userType }) {
    const numberOfTotalTileAds = (getAdData({ uriRef, adKey: TILE_AD_KEY, userType }) || []).length;

    /**
     * if we cant find a limit in our limit map just use the number of ads from the ad map
     * adLimitMap is keyed by page size selected (120, 60) not number of tiles shown (ie 116, 58)
     */
    const adLimit = adLimitMap[pageSize] || numberOfTotalTileAds;
    const numberOfTileAds = numberOfTotalTileAds <= adLimit ? numberOfTotalTileAds : adLimit;

    return numberOfTileAds;
}

export function getNumberOfTiles({
    path,
    pageSize = QP_PAGE_SIZE_VALUE_DEFAULT,
    isSbAdsEnabled = true,
    userType,
}) {
    if (isSbAdsEnabled) {
        return pageSize - getAdCountForPage({ uriRef: path, pageSize, userType });
    }
    return pageSize;
}

// Stole this logic from .com
// https://github.com/1stdibs/1stdibs.com/blob/master/dibs/assets/js/finding/general/placementAds.js#L87
const getProductTileAdIndex = (adIndex, pageCol = 3, adRow = 8) => {
    let slotIndex;
    // (num of columns) * (row to appear in) offset by one
    const initialOffset = adIndex + 1;
    const numCols = pageCol * adRow * initialOffset;
    slotIndex = numCols - 1;

    // ad position offset. Moves the ad from left to right of row
    const offset = pageCol % 2 === 0 ? 0 : adIndex;
    slotIndex -= offset;

    return slotIndex;
};

const shouldGetProductTileAds = ({ numOfTiles }) => {
    if (numOfTiles === QP_PAGE_SIZE_VALUE_DEFAULT || numOfTiles === QP_PAGE_SIZE_VALUE_MAX) {
        return false;
    }

    return true;
};

export const getPageSizeOfTiles = ({ uriRef, numOfTiles, userType }) => {
    // tilesToPageSizeMapForPage is indexed by numb of tiles (pageSize - adCount) to pageSize
    // e.g.{ 58: 60, 116: 120 } or { 58: 60, 118: 120 } (for art only because it has 2 ads max) or { 60: 60, 120: 120 }
    const tilesToPageSizeMapForPage = PAGE_SIZE_OPTIONS.reduce((map, option) => {
        const adCount = getAdCountForPage({ uriRef, pageSize: option.size, userType });
        map[option.size - adCount] = option.size;
        return map;
    }, {});

    return tilesToPageSizeMapForPage[numOfTiles];
};

export const getProductTileAdMap = ({ uriRef, pageCol, adRow, pageSize: numOfTiles, userType }) => {
    if (!numOfTiles || !shouldGetProductTileAds({ numOfTiles })) {
        return {};
    }

    const productTileAdData = getAdData({ uriRef, adKey: TILE_AD_KEY, userType }) || [];

    const adLimit = getAdCountForPage({
        uriRef,
        pageSize: getPageSizeOfTiles({ uriRef, numOfTiles, userType }), // we need to map numOfTiles back to original pageSize to get correct adLimit
        userType,
    });

    return (
        productTileAdData
            // limit by number of ads per page size
            .slice(0, adLimit)
            // get index
            .reduce((map, data, i) => {
                map[getProductTileAdIndex(i, pageCol, adRow)] = data;

                return map;
            }, {})
    );
};

export const ENABLER_KEY_SB_ADS = 'SB_ADS';
export async function shouldEnableSbAds({ getEnabler, urlSearchParams }) {
    const enabler = await getEnabler();
    let isEnabled = enabler.isEnabledForLocale(ENABLER_KEY_SB_ADS);
    if (urlSearchParams.has(getPerfQp(perfFlags.DISABLE_SB_ADS))) {
        isEnabled = false;
    }
    return isEnabled;
}
